
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Getter, Action } from 'vuex-class'
import {
  FocusArtist,
  MusicServiceLookupItem
} from '@/types/music'
import SpotifyItemSelector from '@/components/common/SpotifyItemSelector.vue'
import ItemList from '@/components/common/ItemList.vue'
import DisplayItem from '@/components/common/DisplayItem.vue'

@Component({
  components: {
    SpotifyItemSelector,
    ItemList,
    DisplayItem
  }
})
export default class FocusArtists extends Vue {
  @Getter('campaigns/currentId') campaignId!: string
  @Getter('music/focusArtists') storeFocusArtists!: FocusArtist[]
  @Getter('music/focusArtistsLoading') loading!: boolean

  @Action('music/LOAD_FOCUS_ARTISTS') loadFocusArtists!: () => Promise<void>
  @Action('music/REPLACE_FOCUS_ARTISTS') replaceFocusArtists!: (artists: FocusArtist[]) => Promise<FocusArtist[]>

  localFocusArtists: FocusArtist[] = []

  get focusArtists () {
    return this.localFocusArtists
  }

  async created () {
    await this.loadFocusArtists()
    this.localFocusArtists = [...this.storeFocusArtists]
  }

  @Watch('storeFocusArtists')
  onStoreFocusArtistsChange (newVal: FocusArtist[]) {
    this.localFocusArtists = [...newVal]
  }

  getArtistSubtitle (artist: FocusArtist): string {
    return artist.service ? artist.service.charAt(0).toUpperCase() + artist.service.slice(1) : 'Artist'
  }

  getArtistPosition (artist: FocusArtist): number {
    return this.focusArtists.findIndex(a => a.id === artist.id && a.service === artist.service) + 1
  }

  async addArtist (item: MusicServiceLookupItem) {
    console.log('Adding artist', item)
    try {
      const newArtist: FocusArtist = {
        service: item.service,
        id: item.id,
        market: item.market,
        type: 'artist',
        name: item.name,
        label: item.label,
        url: item.url,
        image: item.image
      }

      const updatedArtists = [...this.localFocusArtists, newArtist]
      await this.replaceFocusArtists(updatedArtists)

      this.$store.dispatch('snackbar/SHOW_MESSAGE',
        { content: 'Focus artist added!', color: 'success' },
        { root: true }
      )
    } catch (error) {
      console.error('Failed to add artist:', error)
      this.$store.dispatch('snackbar/SHOW_MESSAGE',
        { content: 'Failed to add focus artist', color: 'error' },
        { root: true }
      )
    }
  }

  updateFocusArtists (newArtists: FocusArtist[]) {
    // This handles the v-model update from ItemList when items are reordered
    this.localFocusArtists = newArtists
  }

  async onReorder (reorderedArtists: FocusArtist[]) {
    try {
      // Use the reordered array passed from the ItemList component
      await this.replaceFocusArtists(reorderedArtists)
    } catch (error) {
      console.error('Failed to reorder artists:', error)
      this.$store.dispatch('snackbar/SHOW_MESSAGE',
        { content: 'Failed to reorder focus artists', color: 'error' },
        { root: true }
      )
      // Reload to reset order
      await this.loadFocusArtists()
    }
  }

  confirmRemoveArtist (artist: FocusArtist, index: number) {
    if (confirm(`Remove ${artist.name} from focus artists?`)) {
      this.removeArtist({ item: artist, index })
    }
  }

  async removeArtist ({ item: artist }: { item: FocusArtist; index: number }) {
    try {
      const updatedArtists = this.localFocusArtists.filter(a => !(a.id === artist.id && a.service === artist.service))
      await this.replaceFocusArtists(updatedArtists)
      this.$store.dispatch('snackbar/SHOW_MESSAGE',
        { content: 'Focus artist removed!', color: 'success' },
        { root: true }
      )
    } catch (error) {
      console.error('Failed to remove artist:', error)
      this.$store.dispatch('snackbar/SHOW_MESSAGE',
        { content: 'Failed to remove focus artist', color: 'error' },
        { root: true }
      )
    }
  }
}
